import React from 'react';
import { Col, DatePicker, Form, Select } from 'antd';
import { useAuth } from '@/contexts/Auth';
import { ListActionsBar } from '@/components/_components/ListActionsBar';
import { FilterBlock } from '@/components/FilterBlock';
import {
    fetchCharges,
    FiltersCharge,
    useGetCharge,
} from '@/store/invoice/useGetCharge';
import { useGetAllStore } from '@/store/Store';
import { chargesColumns, withoutShopmanagerColumns } from '../columns';
import { INVOICE_MONITORED_FIELDS } from '../const';
import { exportCharges } from '../schemaFile';
import { ChargesFormValues } from '../types';
import { useListTab } from '../useListTab';
import * as S from '../styled';
import { AdminShopmanagerFields } from './AdminShopmanagerFields';
import { ExportPanel } from './ExportPanel';
import { ResultsTable } from './ResultsTable';

type AppliedFilters = Pick<
    FiltersCharge,
    'date' | 'stores[]' | 'shopmanagerId' | 'email'
>;

const toAppliedFilters = (values: ChargesFormValues): AppliedFilters => ({
    date: values.month?.startOf('month').format('YYYY-MM-DD'),
    'stores[]': values.stores?.length
        ? values.stores
        : values.storeId
        ? [values.storeId]
        : undefined,
    shopmanagerId: values.shopManagerId,
    email: values.email,
});

const EMPTY_FILTERS: AppliedFilters = {
    date: undefined,
    'stores[]': undefined,
    shopmanagerId: undefined,
    email: undefined,
};

export function ChargesTab() {
    const { isAdmin } = useAuth();
    const [form] = Form.useForm<ChargesFormValues>();
    const {
        filters,
        pagination,
        onSearch,
        onReset,
        onPageChange,
        onExport,
        isExporting,
    } = useListTab({
        form,
        emptyFilters: EMPTY_FILTERS,
        toAppliedFilters,
        fetchPage: (appliedFilters, page, perPage) =>
            fetchCharges({ ...appliedFilters, page, perPage }),
        exportRows: (rows) => exportCharges(rows, isAdmin),
        exportErrorMessage: 'Falha ao exportar cobranças!',
    });

    const { data: stores = [], isLoading: isLoadingStores } = useGetAllStore();

    const {
        data: charges,
        isFetching,
        isError,
        refetch,
    } = useGetCharge({ ...filters, ...pagination }, true);

    return (
        <S.TabContent>
            <Form
                form={form}
                layout="vertical"
                initialValues={{ showFilter: 'filter' }}
                onFinish={onSearch}
            >
                <ListActionsBar
                    monitoredFields={INVOICE_MONITORED_FIELDS}
                    exportContent={
                        <ExportPanel
                            tabLabel="Cobranças"
                            disabled={isFetching || !charges?.data.length}
                            loading={isExporting}
                            onGenerate={onExport}
                        />
                    }
                >
                    <FilterBlock
                        onReset={onReset}
                        loading={isFetching}
                        justify="start"
                    >
                        {isAdmin && <AdminShopmanagerFields />}
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item<ChargesFormValues>
                                label="Mês"
                                name="month"
                            >
                                <DatePicker
                                    picker="month"
                                    format="MMMM/YYYY"
                                    placeholder="Selecionar mês"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item<ChargesFormValues>
                                label="Loja"
                                name={isAdmin ? 'stores' : 'storeId'}
                            >
                                <Select
                                    mode={isAdmin ? 'multiple' : undefined}
                                    maxTagCount={
                                        isAdmin ? 'responsive' : undefined
                                    }
                                    placeholder="Selecionar loja"
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    loading={isLoadingStores}
                                    options={stores.map((store) => ({
                                        label: store.name,
                                        value: store.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </FilterBlock>
                </ListActionsBar>
            </Form>

            <ResultsTable
                rowKey={(row) => `${row.store_id}-${row.reference_date}`}
                columns={
                    isAdmin
                        ? chargesColumns
                        : withoutShopmanagerColumns(chargesColumns)
                }
                data={charges?.data ?? []}
                total={charges?.meta.total ?? 0}
                loading={isFetching}
                pagination={pagination}
                onPageChange={onPageChange}
                onReset={onReset}
                isError={isError}
                onRetry={refetch}
                emptyText="Nenhuma cobrança encontrada para os filtros selecionados."
                errorText="Falha ao buscar cobranças."
            />
        </S.TabContent>
    );
}
